import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { awayJersey, homeJersey } from '@/common/assets';
import { UrlParams } from '@/common/enums';
import { useLanguage } from '@/common/hooks';
import { useNewTranslations } from '@/common/translations';
import { getUrlParam } from '@/common/utils';

import { TeamInfoApiDataSchema, WidgetsSchema } from '../schema';
import type { TeamInfoData } from '../types';
import { ApiWidgetType, StatsTypes } from '../constants';
import getData from '../utils/getData';
import mapTeamInfoApiDataToTeamInfoData from '../utils/mapTeamInfoApiDataToTeamInfoData';

const StatTypeToApiWidgetTypeMap = {
  [StatsTypes.OVERVIEW]: ApiWidgetType.OVERVIEW,
  [StatsTypes.PREVIOUS_MEETINGS]: ApiWidgetType.PREVIOUS_MEETINGS,
  [StatsTypes.VICTORY_PROBABILITY]: ApiWidgetType.VICTORY_PROBABILITY,
  [StatsTypes.LAST_5_GAMES]: ApiWidgetType.LAST_5_GAMES,
  [StatsTypes.TOP_SCORERS]: ApiWidgetType.TOP_SCORERS,
  [StatsTypes.SEASON_STATS]: ApiWidgetType.SEASON_STATS,
  [StatsTypes.GOALS]: ApiWidgetType.GOALS,
  [StatsTypes.OVER_GOALS]: ApiWidgetType.OVER_GOALS,
  [StatsTypes.SHOTS]: ApiWidgetType.SHOTS,
  [StatsTypes.AVERAGE_MATCH_STATS]: ApiWidgetType.AVERAGE_MATCH_STATS,
  [StatsTypes.VENUE_REFEREE]: ApiWidgetType.VENUE_REFEREE,
  [StatsTypes.LAST_5_GAMES_COMPACT]: ApiWidgetType.LAST_5_GAMES_COMPACT,
  [StatsTypes.LEAGUE_TABLE]: ApiWidgetType.LEAGUE_TABLE,
  [StatsTypes.PLAYER_STATS]: ApiWidgetType.PLAYER_STATS,
  [StatsTypes.LINEUP]: ApiWidgetType.LINEUP,
  // TODO: match row upcoming, match row finished, popular odds
} as const;

interface UseStatsDataParams {
  shouldFetchTeamsData?: boolean;
  widgetType: (typeof StatsTypes)[keyof typeof StatsTypes];
}

const useStatsData = (params: UseStatsDataParams) => {
  const { shouldFetchTeamsData = true, widgetType } = params || {};

  const eventId = getUrlParam(UrlParams.EVENT_ID) || '';
  const translate = useNewTranslations();
  const language = useLanguage();

  const teamsQuery = useQuery({
    enabled: shouldFetchTeamsData,
    queryKey: ['prematch', 'teams', eventId, language],
    queryFn: () =>
      getData({
        path: 'team-info',
        eventId,
        language,
        schema: TeamInfoApiDataSchema,
      }),
    placeholderData: keepPreviousData,
    retry: 2,
    staleTime: Infinity,
  });

  const widgetDataQuery = useQuery({
    queryKey: ['prematch', 'widgets', eventId, language],
    queryFn: () =>
      getData({
        path: 'widgets',
        queryParams: { types: StatTypeToApiWidgetTypeMap[widgetType] },
        eventId,
        language,
        schema: WidgetsSchema,
      }),
    placeholderData: keepPreviousData,
    retry: 2,
    staleTime: Infinity,
  });

  const teamInfo: TeamInfoData = useMemo(() => {
    if (teamsQuery.data) {
      return mapTeamInfoApiDataToTeamInfoData(teamsQuery.data);
    }

    return {
      home: {
        name: translate('PreMatchStats.Home'),
        jersey: homeJersey,
        isNationalTeam: false,
      },
      away: {
        name: translate('PreMatchStats.Away'),
        jersey: awayJersey,
        isNationalTeam: false,
      },
      lineupAvailable: false,
    };
  }, [translate, teamsQuery.data]);

  return useMemo(
    () => ({
      teams: teamInfo,
      widget:
        widgetDataQuery.data?.widgets.find(
          ({ type }) => type === StatTypeToApiWidgetTypeMap[widgetType],
        ) || null,
      isLoading: teamsQuery.isLoading || widgetDataQuery.isLoading,
      isError: teamsQuery.isError || widgetDataQuery.isError,
    }),
    [
      widgetType,
      teamInfo,
      widgetDataQuery.data,
      teamsQuery.isLoading,
      widgetDataQuery.isLoading,
      teamsQuery.isError,
      widgetDataQuery.isError,
    ],
  );
};

export default useStatsData;
